import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { CenterListItemType } from '@/api/type/types';
import { getCountryNameByCode } from '@/utils/name/getCountryName';
import { fonts } from '@/theme/fonts';
import { formatInternationalNumber } from '@/utils/number/formatInternationalNumber';
import { formatDate } from '@/utils/date-time/formatDate';
import { useLoading } from '@/context/LoadingContenxt';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { Images } from '@/assets';
import { partner } from '@/stores/slices/partner-list/partnerSlice';
import { getCenterDetail } from '@/api/partner-list/center/getCenterDetail';
import CenterDetail from './CenterDetail';
import { useTranslation } from 'react-i18next';

interface CenterListItemProps {
  items: CenterListItemType[];
}

const CenterListItem = ({ items }: CenterListItemProps) => {
  const { t } = useTranslation();
  const columns = [
    { key: 'contactCountryCode', label: t('common.country'), width: '10%' },
    { key: 'loginId', label: 'ID', width: '15%' },
    { key: 'name', label: t('common.representativeName'), width: '15%' },
    { key: 'centerName', label: t('centerList.centerName'), width: '15%' },
    { key: 'phoneNumber', label: t('common.mobileNumber'), width: '15%' },
    { key: 'centerNumber', label: t('centerList.centerNumber'), width: '15%' },
    { key: 'registeredAt', label: t('common.registeredDate'), width: '7.5%' },
    { key: 'status', label: t('common.status'), width: '7.5%' },
  ] as const;
  const [openDetailMap, setOpenDetailMap] = useState<Record<string, boolean>>(
    {}
  );
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useAppDispatch();
  const { centerDetail } = useAppSelector((state) => state.partner);

  const valueRenderMap: Record<
    (typeof columns)[number]['key'],
    (row: CenterListItemType) => React.ReactNode
  > = {
    contactCountryCode: (row) =>
      getCountryNameByCode({
        contactCountryCode: row.contact.contactCountryCode,
        countryCode: row.contact.countryCode,
      }),
    loginId: (row) => row.loginId,
    name: (row) => row.representativeName,
    centerName: (row) => row.centerName,
    phoneNumber: (row) =>
      formatInternationalNumber({
        countryCode: row.contact.contactCountryCode,
        number: row.contact.contactNumber,
      }),
    centerNumber: (row) =>
      formatInternationalNumber({
        countryCode: row.centerContact.contactCountryCode,
        number: row.centerContact.contactNumber,
      }),
    registeredAt: (row) => formatDate({ dateString: row.registeredAt }),
    status: (row) => (
      <StatusBadge $status={row.status}>
        {row.status === 'NORMAL' ? t('common.active') : t('common.inactive')}
      </StatusBadge>
    ),
  };

  const onClickDetail = useCallback(
    (id: string) => async () => {
      startLoading();
      try {
        const res = await getCenterDetail({ centerId: id });
        if (res.isSuccess) {
          dispatch(partner.setCenterDetail({ id: id, data: res.data }));
          setOpenDetailMap((prev) => ({
            ...prev,
            [id]: !prev[id],
          }));
        }
      } finally {
        stopLoading();
      }
    },
    [dispatch, startLoading, stopLoading]
  );

  return (
    <TableWrap>
      <TableElement>
        <Thead>
          <tr>
            {columns.map((col) => (
              <Th key={col.key}>{col.label}</Th>
            ))}
          </tr>
        </Thead>
        {items.length > 0 && (
          <tbody>
            {items.map((row) => (
              <React.Fragment key={row.id}>
                <Tr onClick={onClickDetail(row.id)}>
                  {columns.map((col) => (
                    <Td key={col.key}>{valueRenderMap[col.key](row)}</Td>
                  ))}
                </Tr>
                {openDetailMap[row.id] && (
                  <DetailTr key={`detail-${row.id}`}>
                    <DetailTd colSpan={columns.length}>
                      <CenterDetail
                        key={row.id}
                        detail={centerDetail[row.id] ?? null}
                      />
                    </DetailTd>
                  </DetailTr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        )}
      </TableElement>
      {items.length === 0 && (
        <NoneContainer>
          <NoneImg src={Images.NoneDataImg} alt="center-list-none" />
          <div>{t('common.noData')}</div>
        </NoneContainer>
      )}
    </TableWrap>
  );
};

export default CenterListItem;

const TableWrap = styled.div`
  width: 100%;
  overflow: hidden;
  overflow-x: auto;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const TableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: #eef2f7;
`;

const Th = styled.th`
  padding: 12px 16px;
  font-family: ${fonts.family.inter};
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  color: #6b7280;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 14px 16px;
  font-family: ${fonts.family.inter};
  font-size: 14px;
  color: #374151;
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
`;

const Tr = styled.tr`
  cursor: pointer;
  &:hover {
    background: #eef2f7;
  }
`;

const DetailTd = styled.td`
  padding: 16px;
  background: #eef2f7;
  border-bottom: 1px solid #e5e7eb;
`;
const DetailTr = styled.tr``;

const NoneContainer = styled.div`
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-size: 14px;
  color: #6b7280;
`;

const NoneImg = styled.img`
  width: 120px;
  height: 120px;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: ${fonts.weight.semiBold};
  background: ${({ $status }) =>
    $status === 'NORMAL' ? '#E8F8EF' : '#F3F4F6'};
  color: ${({ $status }) => ($status === 'NORMAL' ? '#16A34A' : '#727883')};
`;
